import React, {useState, useEffect} from 'react';
import post from '../utils/post.js';
import AppHeader from '../components/AppHeader.js';
import Search from '../components/Search.js';
import MusclegroupNew from '../components/MusclegroupNew.js';
import MusclegroupEdit from '../components/MusclegroupEdit.js';
import ChartArea from '../components/ChartArea.js';
import ConfirmPopup from '../components/ConfirmPopup.js';

const formatGroupCount = (count) => {
	if (count === 1) {
		return '1 bod';
	}

	if (count >= 2 && count <= 4) {
		return `${count} body`;
	}

	return `${count} bodů`;
};

const formatWorkoutCount = (count) => {
	if (count === 1) {
		return '1 cvik';
	}

	if (count >= 2 && count <= 4) {
		return `${count} cviky`;
	}

	return `${count} cviků`;
};

export default function Musclegroup({usrInfo, timerCnt}) {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [targetUsrName, setTargetUsrName] = useState('');
	const [dataDetail, setDataDetail] = useState({});
	const [showNew, setShowNew] = useState(false);
	const [showEdit, setShowEdit] = useState(false);
	const [targetUsrId, setTargetUsrId] = useState(0);
	const [showImportPopup, setShowImportPopup] = useState(false);
	const [importSearchValue, setImportSearchValue] = useState('');
	const [importLoading, setImportLoading] = useState(false);
	const [importSaving, setImportSaving] = useState(false);
	const [importData, setImportData] = useState([]);
	const [selectedImportIds, setSelectedImportIds] = useState({});

	useEffect(() => {
		const pathUsrId = parseInt(window.location.pathname.split('/')[2], 10);
		const nextTargetUsrId = Number.isInteger(pathUsrId) && pathUsrId > 0 ? pathUsrId : 0;

		setTargetUsrId(nextTargetUsrId);
		getData('', nextTargetUsrId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getData = (search, requestedUsrId = targetUsrId) => {
		setLoading(true);
		const payload = {search: search};

		if (requestedUsrId > 0) {
			payload.USRID = requestedUsrId;
		}

		post('getListMusclegroup', payload)
			.then((res) => {
				setData(res.data.data);
				setTargetUsrName(res.data.target_usr_name || '');
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const handleNew = () => {
		setShowNew(true);
	};

	const isInstructorClientView = targetUsrId > 0 && targetUsrId !== (parseInt(usrInfo.usrId, 10) || 0);

	const closeNew = () => {
		setShowNew(false);
	};

	const openImportPopup = () => {
		setImportSearchValue('');
		setSelectedImportIds({});
		setImportLoading(true);
		setShowImportPopup(true);

		post('getListMusclegroup', {search: ''})
			.then((res) => {
				setImportData(res.data.data || []);
				setImportLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setImportLoading(false);
			});
	};

	const closeImportPopup = () => {
		if (importSaving) {
			return;
		}

		setShowImportPopup(false);
		setImportSearchValue('');
		setSelectedImportIds({});
	};

	const resetImportPopup = () => {
		setShowImportPopup(false);
		setImportSearchValue('');
		setSelectedImportIds({});
	};

	const updateMusclegroupRowNew = (row) => {
		setData([row, ...data]);
	};

	const handleEdit = (rec) => {
		setShowEdit(true);
		setDataDetail(rec);
	};

	const closeEdit = () => {
		setShowEdit(false);
	};

	const updateMusclegroupRowEdit = (row) => {
		const newData = data.map((item) => {
			if (item.ID === row.ID) {
				return row;
			}

			return item;
		});

		setData(newData);
	};

	const updateMusclegroupRowDelete = (row) => {
		const newData = data.filter((item) => {
			return item.ID !== row;
		});

		setData(newData);
	};

	const toggleImportSelection = (muscleId) => {
		setSelectedImportIds((prevState) => {
			const newState = {...prevState};

			if (newState[muscleId]) {
				delete newState[muscleId];
			} else {
				newState[muscleId] = true;
			}

			return newState;
		});
	};

	const handleImportRowToggle = (muscleId, disabled = false) => {
		if (importSaving) {
			return;
		}

		toggleImportSelection(muscleId);
	};

	const filteredImportData = importData.filter((item) => {
		const normalizedSearch = importSearchValue.trim().toLowerCase();

		if (normalizedSearch === '') {
			return true;
		}

		return String(item.NAME || '').toLowerCase().includes(normalizedSearch);
	});

	const selectedSourceIds = Object.keys(selectedImportIds)
		.filter((key) => selectedImportIds[key])
		.map((key) => parseInt(key, 10))
		.filter((key) => Number.isInteger(key) && key > 0);

	const handleImport = () => {
		if (selectedSourceIds.length === 0) {
			return;
		}

		setImportSaving(true);

		post('importMusclegroup', {USRID: targetUsrId, SOURCEIDS: selectedSourceIds})
			.then(() => {
				resetImportPopup();
				setImportSaving(false);
				getData('', targetUsrId);
			})
			.catch((error) => {
				console.error(error);
				setImportSaving(false);
			});
	};

	const totalDataPoints = data.reduce((count, item) => count + (item.data_graph?.data?.length || 0), 0);

	const list = data.map((item, index) => {
		const pointCount = item.data_graph?.data?.length || 0;

		return (
			<section className="musclegroup-page__section" key={'item_' + index} style={{'--musclegroup-accent': item.COLOR || '#8acaa3'}}>
				<div className="musclegroup-page__section-head">
					<div className="musclegroup-page__section-head-main">
						<div className="musclegroup-page__section-kicker">Svalová partie</div>
						<h2 className="musclegroup-page__section-title">{item.NAME}</h2>
					</div>
					<div className="musclegroup-page__section-tools">
						<div className="musclegroup-page__section-total">{formatGroupCount(pointCount)}</div>
						<button type="button" className="musclegroup-page__icon-button" onClick={() => handleEdit(item)} aria-label={`Upravit svalovou partii ${item.NAME}`}>
							<img className="icon1 edit3" alt="" />
						</button>
					</div>
				</div>

				<div className="musclegroup-page__chart-wrap">
					<ChartArea className="musclegroup-page__chart" data={item.data_graph.data} labels={item.data_graph.labels} color={item.COLOR} linesColor={'#c8d4ce'} variant="glass" height="100%" />
				</div>
			</section>
		);
	});

	return (
		<>
			<div className="page_main">
				<AppHeader usrInfo={usrInfo} timerCnt={timerCnt} loading={loading} />
				<ConfirmPopup
					showConfirmPopup={showImportPopup}
					closeConfirmPopup={closeImportPopup}
					infoHeader="Přetáhnout svalové partie"
					infoMain=""
					popupContentClassName="musclegroup-import-popup reservation-popup__content"
					bodyClassName="musclegroup-import-popup__body popup_content_in_body--hidden-scrollbar"
					bodyInnerClassName="musclegroup-import-popup__body-inner"
					footerChildren={(
						<button type="button" className="button green mr_2 musclegroup-import-popup__submit" onClick={handleImport} disabled={importSaving || selectedSourceIds.length === 0}>
							Přidat
						</button>
					)}
				>
					<div className="musclegroup-import-popup__content">
						<div className="musclegroup-import-popup__search-wrap">
							<input
								className="input-text musclegroup-import-popup__search"
								type="text"
								placeholder="Vyhledat svalovou partii"
								value={importSearchValue}
								onChange={(e) => setImportSearchValue(e.target.value)}
								disabled={importSaving}
							/>
						</div>

						<div className="musclegroup-import-popup__list">
							{importLoading ? <div className="musclegroup-import-popup__empty">Načítám svalové partie.</div> : ''}
							{!importLoading && filteredImportData.length === 0 ? <div className="musclegroup-import-popup__empty">Žádná svalová partie neodpovídá hledání.</div> : ''}
							{!importLoading ? filteredImportData.map((item) => {
								const muscleId = parseInt(item.ID, 10) || 0;
								const isSelected = Boolean(selectedImportIds[muscleId]);

								return (
									<div className={`musclegroup-import-popup__row${isSelected ? ' is-selected' : ''}`} key={muscleId} onClick={() => handleImportRowToggle(muscleId)}>
										<button type="button" className={`button check ${isSelected ? 'green' : ''}`} disabled={importSaving} aria-label={isSelected ? `Odebrat ${item.NAME}` : `Vybrat ${item.NAME}`}>
											&nbsp;
										</button>
										<span>{item.NAME} ({formatWorkoutCount(parseInt(item.WORKOUT_CNT, 10) || 0)})</span>
									</div>
								);
							}) : ''}
						</div>
					</div>
				</ConfirmPopup>

				<Search
					showNew
					handleNew={handleNew}
					actionButtons={isInstructorClientView ? (
						<button type="button" className="button small search-new-button" onClick={openImportPopup}>
							Přetáhnout
						</button>
					) : null}
				/>
				<MusclegroupNew showNew={showNew} closeNew={closeNew} updateMusclegroupRowNew={updateMusclegroupRowNew} targetUsrId={targetUsrId} />
				<MusclegroupEdit showEdit={showEdit} closeEdit={closeEdit} updateMusclegroupRowEdit={updateMusclegroupRowEdit} updateMusclegroupRowDelete={updateMusclegroupRowDelete} data={dataDetail} targetUsrId={targetUsrId} />

				<div className={`page musclegroup-page${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''}`}>
					<section className="musclegroup-page__hero">
						<div className="musclegroup-page__hero-main">
							<h1 className="musclegroup-page__title">Přehled svalových partií</h1>
							{isInstructorClientView && targetUsrName !== '' ? <div className="musclegroup-page__client-name">{targetUsrName}</div> : null}
						</div>
						<div className="musclegroup-page__summary-grid">
							<div className="musclegroup-page__summary-card">
								<div className="musclegroup-page__summary-label">Partie</div>
								<div className="musclegroup-page__summary-value">{data.length}</div>
							</div>
							<div className="musclegroup-page__summary-card">
								<div className="musclegroup-page__summary-label">Bodů v grafech</div>
								<div className="musclegroup-page__summary-value">{totalDataPoints}</div>
							</div>
						</div>
					</section>

					<div className="musclegroup-page__shell">
						{list.length > 0 ? list : <div className="musclegroup-page__empty">Zatím nejsou uložené žádné svalové partie.</div>}
					</div>
				</div>
			</div>
		</>
	);
}
